import { forwardRef, useId } from 'react';

/** Half band (the band is 2.2 badges wide), tilted by 10°, as the landing page badge shimmer. */
const TILT = (10 * Math.PI) / 180;
const REACH_X = 1.1 * Math.cos(TILT);
const REACH_Y = 1.1 * Math.sin(TILT);

/** The core starts just before the badge and leaves just after it: the glint fully enters and exits. */
const MARGIN = 0.2;

/** Gradient bounds of the glint at `clock` (0..1 over a cycle), for its window `at`. */
export const glintPosition = (clock, at) => {
  const t = Math.min(Math.max((clock - at[0]) / (at[1] - at[0]), 0), 1);
  const core = -MARGIN + (1 + 2 * MARGIN) * t * t * (3 - 2 * t);
  return { x1: core - REACH_X, x2: core + REACH_X };
};

/**
 * Light band that sweeps once across the round badge of an empty state, once its glyph is drawn.
 * Placed behind the glyph, it brightens the badge without washing out the strokes. The parent
 * animates it by writing `x1` / `x2` on the forwarded gradient (`glintPosition`).
 */
const Glint = forwardRef((props, gradientRef) => {
  // useId yields characters that are not allowed in a `url(#…)` reference.
  const id = `glint${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const start = glintPosition(0, [0, 1]);

  return (
    <svg
      className="absolute inset-0 h-full w-full"
      viewBox="0 0 1 1"
      aria-hidden="true"
    >
      <defs>
        <linearGradient
          ref={gradientRef}
          id={id}
          gradientUnits="userSpaceOnUse"
          x1={start.x1}
          x2={start.x2}
          y1={0.5 - REACH_Y}
          y2={0.5 + REACH_Y}
        >
          <stop offset="0.42" stopColor="#fff" stopOpacity={0} />
          <stop offset="0.5" stopColor="#fff" stopOpacity={1} />
          <stop offset="0.58" stopColor="#fff" stopOpacity={0} />
        </linearGradient>
      </defs>
      <circle cx={0.5} cy={0.5} r={0.5} fill={`url(#${id})`} />
    </svg>
  );
});

export default Glint;
